import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Plus, Minus, Check, Timer, RefreshCw, ExternalLink, Info, Search, ChevronRight, ChevronLeft, Play, Youtube, X, Clock, List } from 'lucide-react';
import axios from 'axios';
import { API, getLocalISOString, stripHtml } from '../App';
import { toast } from 'sonner';
import RestTimer from '../components/RestTimer';
import ExerciseTimer from '../components/ExerciseTimer';
import HamburgerMenu from '../components/HamburgerMenu';
import { getExerciseFormData } from '../utils/getExerciseFormData';
import { getExerciseMedia } from '../utils/getExerciseMedia';
import { classifyExercise, findExerciseByName } from '../utils/exerciseIdentity';
import { getQuickExerciseAlternatives, isCurrentExerciseOption } from '../data/exerciseAlternatives';

const LogWorkout = ({ user, workoutType, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const familyRecommendation =
    location.state?.familyRecommendation || null;

  const recommendedWorkoutType = familyRecommendation
    ? familyRecommendation.workout_type
    : null;

  const effectiveWorkoutType =
    recommendedWorkoutType || workoutType || null;

  const [plan, setPlan] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showRestTimer, setShowRestTimer] = useState(false);
  const [showExerciseTimer, setShowExerciseTimer] = useState(false);
  const [exerciseTimerData, setExerciseTimerData] = useState({ name: '', duration: 30 });
  const [showSubstitutions, setShowSubstitutions] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [substitutions, setSubstitutions] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [exerciseSearchQuery, setExerciseSearchQuery] = useState('');
  const [allExercises, setAllExercises] = useState([]);
  const [showYouTubeEmbed, setShowYouTubeEmbed] = useState(false);
  const [youtubeExerciseName, setYoutubeExerciseName] = useState('');
  const [showExerciseDetail, setShowExerciseDetail] = useState(false);
  const [selectedExerciseDetail, setSelectedExerciseDetail] = useState(null);
  const [activeExerciseIndex, setActiveExerciseIndex] = useState(0);
  const [showWorkoutOverview, setShowWorkoutOverview] = useState(false);
  const [temporaryRebuildIndex, setTemporaryRebuildIndex] = useState(0);

  // Function to get YouTube search URL
  const getYouTubeSearchUrl = (exerciseName) => {
    return `https://www.youtube.com/results?search_query=${encodeURIComponent(exerciseName + ' exercise form tutorial')}`;
  };

  // Function to get YouTube embed search URL  
  const getYouTubeEmbedUrl = (exerciseName) => {
    return `https://www.youtube.com/embed?listType=search&list=${encodeURIComponent(exerciseName + ' exercise form')}`;
  };

  // Open YouTube video modal
  const openYouTubeModal = (exerciseName) => {
    setYoutubeExerciseName(exerciseName);
    setShowYouTubeEmbed(true);
  };

  // Generate placeholder image for exercise
  const getExercisePlaceholder = (exerciseName, muscleGroup) => {
    // Return null to use text-based placeholder in the component
    return null;
  };

  const hasVerifiedExerciseCoaching = (exercise) => {
    const exerciseName =
      exercise?.exercise_name ||
      exercise?.name ||
      '';

    if (!exerciseName) {
      return false;
    }

    const muscleGroup =
      exercise?.muscle_group ||
      exercise?.muscle ||
      '';

    return (
      getExerciseFormData(
        exerciseName,
        muscleGroup
      ).curated === true
    );
  };

  const loadExerciseLibrary = async () => {
    try {
      const response = await axios.get(
        `${API}/exercises/all?workout_type=${effectiveWorkoutType}&limit=500`
      );
      const library = (
        response.data.exercises || []
      ).filter(
        hasVerifiedExerciseCoaching
      );

      setAllExercises(library);
      return library;
    } catch (error) {
      console.warn('Exercise library unavailable for quick alternatives:', error);
      setAllExercises([]);
      return [];
    }
  };

  const initializePlanExercises = async (planData) => {
    const initializedExercises = await Promise.all(
      (planData.exercises || []).map(async (ex) => {
        const classification = classifyExercise(ex);

        const recommendedSetsRaw = ex.recommended_sets ?? ex.sets ?? 3;
        const parsedSets = Number.parseInt(recommendedSetsRaw, 10);
        const recommendedSets =
          Number.isFinite(parsedSets) && parsedSets > 0 ? parsedSets : 3;
        const recommendedReps = String(ex.recommended_reps ?? ex.reps ?? '8-12');

        let lastWeight = Number(ex.starting_weight) || 0;
        if (
          !classification.is_cardio &&
          !classification.is_timed &&
          !classification.is_bodyweight
        ) {
          try {
            const historyResponse = await axios.get(
              `${API}/exercises/${encodeURIComponent(ex.exercise_name)}/last-weight`
            );
            if (historyResponse.data.last_weight) {
              lastWeight = historyResponse.data.last_weight;
            }
          } catch (error) {
            console.log('No previous history for', ex.exercise_name);
          }
        }

        const setCount = classification.is_machine_cardio ? 1 : recommendedSets;

        return {
          exercise_id: ex.exercise_id || ex.id || null,
          exercise_name: ex.exercise_name,
          muscle_group: ex.muscle_group || ex.muscle || '',
          equipment: ex.equipment || '',
          image_url: ex.image_url || null,
          notes: ex.form_tip || '',
          recommended_sets: recommendedSets,
          recommended_reps: recommendedReps,
          last_weight: lastWeight,
          ...classification,
          variation_history: [],
          sets: Array.from({ length: setCount }, (_, index) => ({
            set_number: index + 1,
            ...(classification.is_cardio || classification.is_timed
              ? {
                  time: classification.is_machine_cardio
                    ? classification.recommended_time
                    : classification.is_timed
                      ? classification.default_duration / 60
                      : 0,
                  ...(classification.is_machine_cardio
                    ? { distance: 0, calories: 0, incline: 0, speed: 0 }
                    : {}),
                }
              : {
                  reps: 0,
                  weight: classification.is_bodyweight ? 0 : lastWeight,
                }),
            completed: false,
            exercise_name: ex.exercise_name,
          })),
        };
      })
    );

    setExercises(initializedExercises);
    setActiveExerciseIndex(0);
  };

  useEffect(() => {
    loadPlanAndInitialize();
  }, [location, effectiveWorkoutType]);

  const loadPlanAndInitialize = async () => {
    setLoading(true);

    try {
      if (!effectiveWorkoutType) {
        navigate('/workout-selection', { replace: true });
        return;
      }

      const targetDate =
        location.state?.date || getLocalISOString();

      const dateStr = targetDate.split('T')[0];

      console.log(
        'LogWorkout loading plan for date:',
        dateStr
      );

      let planData;

      if (familyRecommendation) {
        const generated = await axios.post(
          `${API}/workouts/generate-plan`,
          {
            workout_type: effectiveWorkoutType,
            date: targetDate,
            focus_override: familyRecommendation.focus,
            persist_plan: false,
          }
        );

        planData = generated.data;
      } else {
        // Only generate when the API explicitly says a plan does not exist.
        // Authentication/network/server failures must not silently replace
        // a workout.
        const response = await axios.get(
          `${API}/workouts/plan/${dateStr}?workout_type=${effectiveWorkoutType}`
        );

        planData = response.data;

        if (
          response.data &&
          response.data.exists === false
        ) {
          const generated = await axios.post(
            `${API}/workouts/generate-plan`,
            {
              workout_type: effectiveWorkoutType,
              date: targetDate,
            }
          );

          planData = generated.data;
        }
      }

      if (
        !planData ||
        !Array.isArray(planData.exercises)
      ) {
        throw new Error(
          'Workout plan response did not contain an exercise list.'
        );
      }

      setPlan(planData);

      await Promise.all([
        initializePlanExercises(planData),
        loadExerciseLibrary(),
      ]);
    } catch (error) {
      const message =
        error?.response?.status === 401
          ? 'Your session expired. Please sign in again.'
          : 'Unable to load this workout. Your existing plan was not replaced.';

      toast.error(message);
      console.error('Error loading workout:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSet = (exerciseIdx, setIdx, field, value) => {
    const newExercises = [...exercises];
    newExercises[exerciseIdx].sets[setIdx][field] = value;
    setExercises(newExercises);
  };

  const regenerateCurrentWorkout = async () => {
    const hasCompletedWork = exercises.some((exercise) =>
      exercise.sets.some((set) => set.completed) ||
      (exercise.variation_history || []).some((variation) =>
        variation.sets.some((set) => set.completed)
      )
    );

    if (hasCompletedWork) {
      toast.info(
        'A workout with completed sets cannot be rebuilt. Finish it or start a new workout instead.'
      );
      return;
    }

    const confirmationMessage = familyRecommendation
      ? 'Generate a different version of this recommended workout?'
      : "Replace today's saved workout with a newly generated workout?";

    const confirmed = window.confirm(
      confirmationMessage
    );

    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      const targetDate =
        location.state?.date ||
        getLocalISOString();

      const nextTemporaryVariation =
        temporaryRebuildIndex + 1;

      const requestBody = familyRecommendation
        ? {
            workout_type: effectiveWorkoutType,
            date: targetDate,
            focus_override: familyRecommendation.focus,
            persist_plan: false,
            force_regenerate: true,
            variation_index: nextTemporaryVariation,
          }
        : {
            workout_type: effectiveWorkoutType,
            date: targetDate,
            force_regenerate: true,
          };

      const generated = await axios.post(
        `${API}/workouts/generate-plan`,
        requestBody
      );

      const planData = generated.data;

      if (
        !planData ||
        !Array.isArray(planData.exercises)
      ) {
        throw new Error(
          'Regenerated workout did not contain an exercise list.'
        );
      }

      if (familyRecommendation) {
        setTemporaryRebuildIndex(
          nextTemporaryVariation
        );
      }

      setPlan(planData);

      await Promise.all([
        initializePlanExercises(planData),
        loadExerciseLibrary(),
      ]);

      setShowWorkoutOverview(false);

      toast.success(
        familyRecommendation
          ? 'Workout rebuilt.'
          : "Today's workout was rebuilt."
      );
    } catch (error) {
      console.error(
        'Unable to rebuild workout:',
        error
      );

      toast.error(
        'This workout could not be rebuilt.'
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleSetComplete = (exerciseIdx, setIdx) => {
    const newExercises = [...exercises];
    const wasCompleted = newExercises[exerciseIdx].sets[setIdx].completed;
    newExercises[exerciseIdx].sets[setIdx].completed = !wasCompleted;
    if (!wasCompleted) {
      newExercises[exerciseIdx].sets[setIdx].exercise_name =
        newExercises[exerciseIdx].exercise_name;
    }
    setExercises(newExercises);
    
    // Show rest timer when a set is completed
    if (!wasCompleted) {
      setShowRestTimer(true);
    }
  };

  const getSubstitutions = async (exerciseName) => {
    try {
      // Get low-impact substitutions
      const response = await axios.get(`${API}/exercises/${encodeURIComponent(exerciseName)}/substitutions`);
      setSubstitutions(response.data);
      
      // Also fetch all exercises for search
      const allExercisesResponse = await axios.get(`${API}/exercises/all?workout_type=${effectiveWorkoutType}`);
      setAllExercises(
        (
          allExercisesResponse.data.exercises || []
        ).filter(
          hasVerifiedExerciseCoaching
        )
      );
      
      setShowSubstitutions(true);
    } catch (error) {
      toast.error('Failed to load exercise options');
    }
  };

  const substituteExercise = async (exerciseIdx, replacement) => {
    const current = exercises[exerciseIdx];
    if (!current) return;

    if (current.sets.length > 0 && current.sets.every((set) => set.completed)) {
      toast.info('This exercise is already complete. Move to the next exercise.');
      return;
    }

    const requestedName =
      typeof replacement === 'string'
        ? replacement
        : replacement?.exercise_name || replacement?.name || '';

    const libraryRecord =
      typeof replacement === 'object'
        ? replacement
        : findExerciseByName(allExercises, requestedName);

    const source = libraryRecord || { exercise_name: requestedName };
    const newExerciseName = source.exercise_name || source.name || requestedName;

    if (!newExerciseName) {
      toast.error('Could not resolve that exercise.');
      return;
    }

    const replacementCoaching =
      getExerciseFormData(
        newExerciseName,
        source.muscle_group ||
          source.muscle ||
          current.muscle_group ||
          ''
      );

    if (!replacementCoaching.curated) {
      toast.error(
        'Verified coaching is not available for that movement.'
      );
      return;
    }

    const recommendedSetsRaw =
      source.recommended_sets ?? source.sets ?? current.recommended_sets ?? 3;
    const parsedSets = Number.parseInt(recommendedSetsRaw, 10);
    const recommendedSets =
      Number.isFinite(parsedSets) && parsedSets > 0 ? parsedSets : 3;

    const recommendedReps =
      source.recommended_reps ?? source.reps ?? current.recommended_reps ?? '8-12';

    const baseReplacement = {
      exercise_id: source.exercise_id || source.id || null,
      exercise_name: newExerciseName,
      muscle_group:
        source.muscle_group || source.muscle || source.category || current.muscle_group || '',
      equipment: source.equipment || '',
      recommended_sets: recommendedSets,
      recommended_reps: String(recommendedReps),
      duration_seconds: source.duration_seconds || 0,
      is_timed: source.is_timed === true,
      is_warmup: source.is_warmup === true,
      is_cooldown: source.is_cooldown === true,
      type: source.type,
      category: source.category,
    };

    const classification = classifyExercise(baseReplacement);

    let lastWeight = 0;
    if (
      !classification.is_cardio &&
      !classification.is_timed &&
      !classification.is_bodyweight
    ) {
      try {
        const historyResponse = await axios.get(
          `${API}/exercises/${encodeURIComponent(newExerciseName)}/last-weight`
        );
        lastWeight = historyResponse.data.last_weight || 0;
      } catch (error) {
        console.log('No previous history for', newExerciseName);
      }
    }

    // Preserve sets already completed under the old movement as a separate
    // history entry. This lets users regress/progress mid-workout without
    // erasing work they already performed.
    const completedSets = current.sets.filter((set) => set.completed);
    const variationHistory = [...(current.variation_history || [])];

    if (completedSets.length > 0) {
      const snapshot = {
        ...current,
        sets: completedSets.map((set) => ({
          ...set,
          exercise_name: set.exercise_name || current.exercise_name,
        })),
      };
      delete snapshot.variation_history;
      variationHistory.push(snapshot);
    }

    const targetSetCount = classification.is_machine_cardio ? 1 : recommendedSets;
    const remainingSetCount = Math.max(targetSetCount - completedSets.length, 1);

    const sets = Array.from({ length: remainingSetCount }, (_, index) => ({
      set_number: completedSets.length + index + 1,
      ...(classification.is_cardio || classification.is_timed
        ? {
            time: classification.is_machine_cardio
              ? classification.recommended_time
              : classification.is_timed
                ? classification.default_duration / 60
                : 0,
            ...(classification.is_machine_cardio
              ? { distance: 0, calories: 0, incline: 0, speed: 0 }
              : {}),
          }
        : {
            reps: 0,
            weight: classification.is_bodyweight ? 0 : lastWeight,
          }),
      completed: false,
      exercise_name: newExerciseName,
    }));

    const replacementNotes = stripHtml(
      source.form_tip || source.instructions || ''
    );

    const newExercises = [...exercises];
    newExercises[exerciseIdx] = {
      ...current,
      ...baseReplacement,
      ...classification,
      image_url: null,
      notes: replacementNotes,
      last_weight: lastWeight,
      variation_history: variationHistory,
      sets,
    };

    setExercises(newExercises);
    setShowSubstitutions(false);
    toast.success(`Exercise changed to ${newExerciseName}`);
  };

  const addSet = (exerciseIdx) => {
    const newExercises = [...exercises];
    const exercise = newExercises[exerciseIdx];
    const lastSet = exercise.sets[exercise.sets.length - 1];
    
    const newSet = {
      set_number: exercise.sets.length + 1,
      completed: false
    };
    
    if (exercise.is_cardio) {
      newSet.time = lastSet?.time || 0;
      newSet.distance = lastSet?.distance || 0;
      newSet.calories = lastSet?.calories || 0;
      // Add incline and speed for machine cardio
      if (exercise.is_machine_cardio) {
        newSet.incline = lastSet?.incline || 0;
        newSet.speed = lastSet?.speed || 0;
      }
    } else {
      newSet.reps = lastSet?.reps || 0;
      newSet.weight = lastSet?.weight || 0;
    }
    
    exercise.sets.push(newSet);
    setExercises(newExercises);
  };

  const removeSet = (exerciseIdx) => {
    const newExercises = [...exercises];
    if (newExercises[exerciseIdx].sets.length > 1) {
      newExercises[exerciseIdx].sets.pop();
      setExercises(newExercises);
    }
  };

  const saveWorkout = async () => {
    // Check if at least one set is completed
    const hasCompletedSets = exercises.some(ex => ex.sets.some(set => set.completed));
    if (!hasCompletedSets) {
      toast.error('Please complete at least one set before saving');
      return;
    }

    setSaving(true);
    try {
      const exercisesForLog = exercises.flatMap((exercise) => {
        const history = exercise.variation_history || [];
        const currentExercise = { ...exercise };
        delete currentExercise.variation_history;
        return [...history, currentExercise];
      });

      const workoutData = {
        workout_type: effectiveWorkoutType,
        date: getLocalISOString(),
        exercises: exercisesForLog,
        completed: true,
        notes: notes,
        focus:
          plan?.focus ||
          familyRecommendation?.focus ||
          null,
      };

      await axios.post(`${API}/workouts/log`, workoutData);
      toast.success('Workout logged successfully! 🏋️‍♀️');
      setTimeout(
        () =>
          navigate(
            familyRecommendation
              ? '/family'
              : '/dashboard'
          ),
        1500
      );
    } catch (error) {
      toast.error('Failed to save workout');
      console.error('Error saving workout:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black flex items-center justify-center">
        <div className="text-blood-400 text-xl font-bold tracking-wider">LOADING BATTLE...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black pb-20 overflow-x-hidden">
      <HamburgerMenu />
      
      <div className="max-w-5xl mx-auto px-4 py-4 animate-fade-in">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl md:text-3xl font-bold text-blood-400 tracking-wider" data-testid="log-workout-title">
              LOG YOUR BATTLE
            </h1>
            <Button
              onClick={saveWorkout}
              disabled={saving}
              size="sm"
              className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold tracking-wider"
              data-testid="save-workout-btn"
            >
              <Check className="w-4 h-4 mr-1" />
              {saving ? 'Saving...' : 'Complete'}
            </Button>
          </div>
          <p className="text-stone-500 tracking-wide text-xs">ACTIVE WORKOUT</p>
          <div
            className="flex flex-wrap gap-2 mt-3"
            data-testid="workout-toolbar"
          >
            <Button
              onClick={() => setShowWorkoutOverview(true)}
              variant="outline"
              size="sm"
              className="glass border-stone-700 text-stone-300 hover:bg-stone-800 text-xs"
              data-testid="workout-overview-btn"
            >
              <List className="w-3 h-3 mr-1" />
              OVERVIEW
            </Button>

            <Button
              onClick={regenerateCurrentWorkout}
              variant="outline"
              size="sm"
              className="glass border-blood-700 text-blood-300 hover:bg-blood-950/50 text-xs font-bold"
              data-testid="regenerate-workout-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              REBUILD WORKOUT
            </Button>

            <Button
              onClick={() => setShowRestTimer(true)}
              variant="outline"
              size="sm"
              className="glass border-stone-700 text-stone-400 hover:bg-stone-800 text-xs"
              data-testid="open-timer-btn"
            >
              <Timer className="w-3 h-3 mr-1" />
              REST TIMER
            </Button>
          </div>
        </div>

        {/* Exercises */}
        <div className="space-y-6">
          {exercises[activeExerciseIndex] && (() => {
            const exercise = exercises[activeExerciseIndex];
            const exerciseIdx = activeExerciseIndex;
            const quickAlternatives = getQuickExerciseAlternatives(
              exercise,
              allExercises,
              { workoutType: effectiveWorkoutType }
            );

            return (
            <Card key={`${exercise.exercise_name}-${exerciseIdx}`} className="glass p-6 border border-stone-800" data-testid={`exercise-log-${exerciseIdx}`}>
              <div className="mb-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-blood-500 text-xs font-bold uppercase tracking-[0.18em]" data-testid="active-exercise-progress">
                    EXERCISE {activeExerciseIndex + 1} OF {exercises.length}
                  </p>
                  <p className="text-stone-600 text-xs">
                    {Math.round(((activeExerciseIndex + 1) / exercises.length) * 100)}%
                  </p>
                </div>
                <div className="w-full h-1.5 bg-stone-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blood-700 transition-all duration-300"
                    style={{ width: `${((activeExerciseIndex + 1) / exercises.length) * 100}%` }}
                    data-testid="workout-progress-bar"
                  />
                </div>
              </div>

              <div className="flex items-start gap-4 mb-4">
                {(() => {
                  const mediaUrl = getExerciseMedia(
                    exercise.exercise_id,
                    exercise.muscle_group,
                    exercise.exercise_name,
                    exercise.image_url
                  );
                  if (!mediaUrl) return null;
                  return (
                    <div className="w-20 h-20 rounded-lg bg-stone-950 border border-stone-800 flex-shrink-0 overflow-hidden">
                      <img
                        src={mediaUrl}
                        alt={`${exercise.exercise_name} form illustration`}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        onError={(event) => {
                          if (event.currentTarget.parentElement) {
                            event.currentTarget.parentElement.style.display = 'none';
                          }
                        }}
                        data-testid={`exercise-media-${exerciseIdx}`}
                      />
                    </div>
                  );
                })()}
                <div className="flex-1">
                  <h3 
                    className="text-xl font-bold text-blood-400 mb-1 tracking-wider" 
                    data-testid={`exercise-name-${exerciseIdx}`}
                  >
                    {exercise.exercise_name.toUpperCase()}
                  </h3>
                  <p className="text-stone-400 text-sm mb-2 uppercase tracking-wide">{exercise.muscle_group}</p>
                  
                  {/* Show workout requirements */}
                  <div className="bg-blood-950/40 rounded-lg p-2 mt-2 border border-blood-900/50">
                    <p className="text-stone-300 text-sm font-semibold">
                      {exercise.is_cardio ? (
                        <span>TARGET: {exercise.recommended_reps}</span>
                      ) : (
                        <>
                          <span className="text-blood-400">{exercise.recommended_reps} REPS</span>
                          {exercise.last_weight > 0 && (
                            <span className="ml-3 text-blood-500">LAST: {exercise.last_weight} LBS</span>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>


              {/* Always-visible exercise replacement action */}
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedExercise(exerciseIdx);
                  getSubstitutions(exercise.exercise_name);
                }}
                className="w-full mb-4 border-blood-800 bg-blood-950/30 hover:bg-blood-900/40 hover:border-blood-600 text-blood-300 hover:text-white font-bold tracking-wider"
                data-testid={`swap-exercise-btn-${exerciseIdx}`}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                SWAP EXERCISE
              </Button>

              {/* Quick movement alternatives */}
              {quickAlternatives.family && (
                <div className="glass p-3 rounded-lg mb-4 border border-stone-800" data-testid="quick-alternatives">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <p className="text-blood-400 text-xs font-bold uppercase tracking-widest">Movement Options</p>
                      <p className="text-stone-500 text-xs mt-1">Adjust the movement without leaving your workout.</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedExercise(exerciseIdx);
                        getSubstitutions(exercise.exercise_name);
                      }}
                      className="text-stone-400 hover:text-blood-400 text-xs"
                      data-testid={`more-alternatives-btn-${exerciseIdx}`}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      ALL OPTIONS
                    </Button>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {[
                      ['Low Impact', quickAlternatives.lowImpact, 'low'],
                      ['Standard', quickAlternatives.standard, 'standard'],
                      ['Advanced', quickAlternatives.advanced, 'advanced'],
                    ].map(([label, candidate, key]) => {
                      const selected = isCurrentExerciseOption(exercise, candidate);
                      return (
                        <button
                          key={key}
                          type="button"
                          disabled={!candidate || selected}
                          onClick={() => candidate && substituteExercise(exerciseIdx, candidate)}
                          className={`rounded-lg border p-2 text-left transition-colors ${
                            selected
                              ? 'border-blood-700 bg-blood-950/40 cursor-default'
                              : candidate
                                ? 'border-stone-700 bg-stone-900/70 hover:border-blood-700'
                                : 'border-stone-800 bg-stone-950/40 opacity-40 cursor-not-allowed'
                          }`}
                          data-testid={`exercise-alternative-${key}-${exerciseIdx}`}
                        >
                          <span className={`block text-[10px] font-bold uppercase tracking-wider ${
                            selected ? 'text-blood-500' : 'text-stone-500'
                          }`}>
                            {label}
                          </span>
                          <span className={`block text-xs mt-1 leading-tight ${
                            selected ? 'text-blood-300' : 'text-stone-200'
                          }`}>
                            {candidate?.name || candidate?.exercise_name || 'Not available'}
                          </span>
                          {selected && (
                            <span className="block text-[9px] text-blood-600 mt-1 uppercase tracking-wider">
                              Current
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Curated form coaching - click to expand */}
              {(() => {
                const formGuide = getExerciseFormData(exercise.exercise_name, exercise.muscle_group);
                return (
                  <div
                    className="glass p-4 rounded-lg mb-4 border border-stone-800 cursor-pointer hover:border-blood-700 transition-colors group"
                    onClick={() => {
                      setSelectedExerciseDetail(exercise);
                      setShowExerciseDetail(true);
                    }}
                    data-testid={`exercise-tip-${exerciseIdx}`}
                  >
                    <div className="flex items-start">
                      <Info className="w-5 h-5 text-blood-600 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-blood-400 font-bold text-sm mb-1 uppercase tracking-wider">
                          {formGuide.curated ? 'Key Form Cue:' : 'Form Guide:'}
                        </p>
                        <p className="text-stone-300 text-sm leading-relaxed">{formGuide.summary}</p>
                        <p className="text-blood-500 text-xs mt-2 group-hover:text-blood-400 flex items-center gap-1">
                          <Plus className="w-3 h-3" />
                          Open setup, execution & key cues
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Sets Table */}
              <div className="space-y-3">
                {exercise.is_timed ? (
                  // Time-based exercise logging (yoga, pilates, holds, stretches)
                  <>
                    {/* Start Exercise Timer Button */}
                    <div className="mb-3">
                      <Button
                        onClick={() => {
                          setExerciseTimerData({
                            name: exercise.exercise_name,
                            duration: exercise.default_duration || 30
                          });
                          setShowExerciseTimer(true);
                        }}
                        className="w-full bg-blood-700 hover:bg-blood-800 text-white font-bold py-3"
                        data-testid={`start-timer-btn-${exerciseIdx}`}
                      >
                        <Clock className="w-5 h-5 mr-2" />
                        START EXERCISE TIMER
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-12 gap-2 text-stone-300 text-sm font-bold mb-2 uppercase tracking-wider">
                      <div className="col-span-2">Set</div>
                      <div className="col-span-6">Duration</div>
                      <div className="col-span-4 text-center">Done</div>
                    </div>
                    {exercise.sets.map((set, setIdx) => (
                      <div key={setIdx} className="grid grid-cols-12 gap-2 items-center" data-testid={`set-row-${exerciseIdx}-${setIdx}`}>
                        <div className="col-span-2 text-blood-400 font-bold">{set.set_number}</div>
                        <div className="col-span-6">
                          <div className="flex items-center gap-2">
                            {/* Quick time buttons */}
                            <div className="flex gap-1">
                              {[30, 45, 60, 90].map(secs => (
                                <button
                                  key={secs}
                                  type="button"
                                  onClick={() => updateSet(exerciseIdx, setIdx, 'time', secs / 60)}
                                  className={`px-2 py-1 text-xs rounded ${
                                    Math.round(set.time * 60) === secs
                                      ? 'bg-blood-600 text-white'
                                      : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
                                  }`}
                                >
                                  {secs}s
                                </button>
                              ))}
                            </div>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={set.time === 0 ? '' : set.time}
                              onChange={(e) => updateSet(exerciseIdx, setIdx, 'time', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                              className="bg-stone-900 border-stone-700 text-white w-20"
                              placeholder="min"
                              data-testid={`time-input-${exerciseIdx}-${setIdx}`}
                            />
                            <span className="text-stone-400 text-sm">min</span>
                          </div>
                        </div>
                        <div className="col-span-4 flex justify-center">
                          <Button
                            size="sm"
                            variant={set.completed ? "default" : "outline"}
                            onClick={() => toggleSetComplete(exerciseIdx, setIdx)}
                            className={set.completed ? 'bg-green-700 hover:bg-green-800 text-white' : 'bg-stone-900 text-stone-300 border-stone-700'}
                            data-testid={`complete-btn-${exerciseIdx}-${setIdx}`}
                          >
                            {set.completed ? <Check className="w-4 h-4" /> : 'Done'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                ) : exercise.is_cardio ? (
                  // Cardio exercise logging
                  <>
                    {exercise.is_machine_cardio ? (
                      // Machine cardio (treadmill, bike, elliptical) - comprehensive fields
                      <div className="space-y-4">
                        {/* Recommended info banner */}
                        <div className="bg-blood-950/30 border border-blood-800/50 rounded-lg p-3 text-sm">
                          <span className="text-blood-400 font-medium">
                            {exercise.is_warmup ? '🔥 Warmup: ' : exercise.is_cooldown ? '❄️ Cool-down: ' : '💪 Cardio: '}
                          </span>
                          <span className="text-stone-300">
                            Recommended {exercise.recommended_time || (exercise.is_warmup ? 10 : 30)} minutes
                          </span>
                        </div>
                        
                        {exercise.sets.map((set, setIdx) => (
                          <div key={setIdx} className="bg-stone-900/50 rounded-lg p-4 space-y-3" data-testid={`set-row-${exerciseIdx}-${setIdx}`}>
                            {/* Row 1: Time, Incline, Speed */}
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="text-stone-400 text-xs font-bold uppercase tracking-wider mb-1 block">Time (min)</label>
                                <Input
                                  type="number"
                                  step="1"
                                  value={set.time === 0 ? '' : set.time}
                                  onChange={(e) => updateSet(exerciseIdx, setIdx, 'time', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                  className="bg-stone-800 border-stone-700 text-white text-lg font-bold"
                                  placeholder={String(exercise.recommended_time || 10)}
                                  data-testid={`time-input-${exerciseIdx}-${setIdx}`}
                                />
                              </div>
                              <div>
                                <label className="text-stone-400 text-xs font-bold uppercase tracking-wider mb-1 block">Incline %</label>
                                <Input
                                  type="number"
                                  step="0.5"
                                  value={set.incline === 0 ? '' : set.incline}
                                  onChange={(e) => updateSet(exerciseIdx, setIdx, 'incline', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                  className="bg-stone-800 border-stone-700 text-white"
                                  placeholder="0"
                                  data-testid={`incline-input-${exerciseIdx}-${setIdx}`}
                                />
                              </div>
                              <div>
                                <label className="text-stone-400 text-xs font-bold uppercase tracking-wider mb-1 block">Speed (mph)</label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={set.speed === 0 ? '' : set.speed}
                                  onChange={(e) => updateSet(exerciseIdx, setIdx, 'speed', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                  className="bg-stone-800 border-stone-700 text-white"
                                  placeholder="3.5"
                                  data-testid={`speed-input-${exerciseIdx}-${setIdx}`}
                                />
                              </div>
                            </div>
                            
                            {/* Row 2: Distance, Calories, Done button */}
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="text-stone-400 text-xs font-bold uppercase tracking-wider mb-1 block">Distance</label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={set.distance === 0 ? '' : set.distance}
                                  onChange={(e) => updateSet(exerciseIdx, setIdx, 'distance', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                  className="bg-stone-800 border-stone-700 text-white"
                                  placeholder="0.5"
                                  data-testid={`distance-input-${exerciseIdx}-${setIdx}`}
                                />
                              </div>
                              <div>
                                <label className="text-stone-400 text-xs font-bold uppercase tracking-wider mb-1 block">Calories</label>
                                <Input
                                  type="number"
                                  value={set.calories === 0 ? '' : set.calories}
                                  onChange={(e) => updateSet(exerciseIdx, setIdx, 'calories', e.target.value === '' ? 0 : parseInt(e.target.value))}
                                  className="bg-stone-800 border-stone-700 text-white"
                                  placeholder="50"
                                  data-testid={`calories-input-${exerciseIdx}-${setIdx}`}
                                />
                              </div>
                              <div className="flex items-end">
                                <Button
                                  variant={set.completed ? 'default' : 'outline'}
                                  size="lg"
                                  onClick={() => toggleSetComplete(exerciseIdx, setIdx)}
                                  className={`w-full ${set.completed ? 'bg-blood-700 hover:bg-blood-800' : 'border-stone-600'}`}
                                  data-testid={`complete-btn-${exerciseIdx}-${setIdx}`}
                                >
                                  {set.completed ? <><Check className="w-4 h-4 mr-1" /> Done</> : 'Mark Done'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Bodyweight cardio (burpees, jumping jacks, etc.) - only show time
                      <>
                        <div className="grid grid-cols-12 gap-2 text-stone-400 text-sm font-bold mb-2 uppercase tracking-wider">
                          <div className="col-span-2">Set</div>
                          <div className="col-span-8">Time (minutes)</div>
                          <div className="col-span-2 text-center">Done</div>
                        </div>
                        {/* Quick time buttons */}
                        <div className="flex gap-2 mb-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              exercise.sets.forEach((_, setIdx) => {
                                updateSet(exerciseIdx, setIdx, 'time', 1);
                              });
                            }}
                            className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-semibold text-xs"
                          >
                            1 MIN ALL
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              exercise.sets.forEach((_, setIdx) => {
                                updateSet(exerciseIdx, setIdx, 'time', 2);
                              });
                            }}
                            className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-semibold text-xs"
                          >
                            2 MIN ALL
                          </Button>
                        </div>
                        {exercise.sets.map((set, setIdx) => (
                          <div key={setIdx} className="grid grid-cols-12 gap-2 items-center" data-testid={`set-row-${exerciseIdx}-${setIdx}`}>
                            <div className="col-span-2 text-blood-400 font-bold">{set.set_number}</div>
                            <div className="col-span-8">
                              <Input
                                type="number"
                                step="0.1"
                                value={set.time === 0 ? '' : set.time}
                                onChange={(e) => updateSet(exerciseIdx, setIdx, 'time', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                className="bg-stone-900 border-stone-700 text-white"
                                placeholder="0"
                                data-testid={`time-input-${exerciseIdx}-${setIdx}`}
                              />
                            </div>
                            <div className="col-span-2 flex justify-center">
                              <Button
                                variant={set.completed ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => toggleSetComplete(exerciseIdx, setIdx)}
                                className={set.completed ? 'bg-blood-700 hover:bg-blood-800' : 'border-stone-600'}
                                data-testid={`complete-btn-${exerciseIdx}-${setIdx}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </>
                ) : exercise.is_bodyweight ? (
                  // Bodyweight exercise logging - REPS ONLY (no weight)
                  <>
                    <div className="grid grid-cols-12 gap-2 text-stone-300 text-sm font-bold mb-2 uppercase tracking-wider">
                      <div className="col-span-2">Set</div>
                      <div className="col-span-8">Reps</div>
                      <div className="col-span-2 text-center">Done</div>
                    </div>
                    {exercise.sets.map((set, setIdx) => (
                      <div key={setIdx} className="grid grid-cols-12 gap-2 items-center" data-testid={`set-row-${exerciseIdx}-${setIdx}`}>
                        <div className="col-span-2 text-blood-400 font-bold">{set.set_number}</div>
                        <div className="col-span-8">
                          <Input
                            type="number"
                            value={set.reps === 0 ? '' : set.reps}
                            onChange={(e) => updateSet(exerciseIdx, setIdx, 'reps', e.target.value === '' ? 0 : parseInt(e.target.value))}
                            className="bg-stone-900 border-stone-700 text-white"
                            placeholder="0"
                            data-testid={`reps-input-${exerciseIdx}-${setIdx}`}
                          />
                        </div>
                        <div className="col-span-2 flex justify-center">
                          <Button
                            variant={set.completed ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => toggleSetComplete(exerciseIdx, setIdx)}
                            className={set.completed ? 'bg-blood-700 hover:bg-blood-800' : 'border-stone-600'}
                            data-testid={`complete-btn-${exerciseIdx}-${setIdx}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  // Regular weighted exercise logging
                  <>
                    <div className="grid grid-cols-12 gap-2 text-stone-300 text-sm font-bold mb-2 uppercase tracking-wider">
                      <div className="col-span-2">Set</div>
                      <div className="col-span-4">Weight (lbs)</div>
                      <div className="col-span-4">Reps</div>
                      <div className="col-span-2 text-center">Done</div>
                    </div>
                    {exercise.sets.map((set, setIdx) => (
                      <div key={setIdx} className="grid grid-cols-12 gap-2 items-center" data-testid={`set-row-${exerciseIdx}-${setIdx}`}>
                        <div className="col-span-2 text-blood-400 font-bold">{set.set_number}</div>
                        <div className="col-span-4">
                          <Input
                            type="number"
                            value={set.weight === 0 ? '' : set.weight}
                            onChange={(e) => updateSet(exerciseIdx, setIdx, 'weight', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            className="bg-stone-900 border-stone-700 text-white"
                            placeholder="0"
                            data-testid={`weight-input-${exerciseIdx}-${setIdx}`}
                          />
                        </div>
                        <div className="col-span-4">
                          <Input
                            type="number"
                            value={set.reps === 0 ? '' : set.reps}
                            onChange={(e) => updateSet(exerciseIdx, setIdx, 'reps', e.target.value === '' ? 0 : parseInt(e.target.value))}
                            className="bg-stone-900 border-stone-700 text-white"
                            placeholder="0"
                            data-testid={`reps-input-${exerciseIdx}-${setIdx}`}
                          />
                        </div>
                        <div className="col-span-2 flex justify-center">
                          <Button
                            variant={set.completed ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => toggleSetComplete(exerciseIdx, setIdx)}
                            className={set.completed ? 'bg-blood-700 hover:bg-blood-800' : 'border-stone-600'}
                            data-testid={`complete-btn-${exerciseIdx}-${setIdx}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Add/Remove Set Buttons */}
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addSet(exerciseIdx)}
                  className="glass border-stone-700 text-stone-400 hover:bg-stone-800 font-semibold"
                  data-testid={`add-set-btn-${exerciseIdx}`}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  ADD SET
                </Button>
                {exercise.sets.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeSet(exerciseIdx)}
                    className="glass border-blood-900 text-blood-400 hover:bg-blood-950/50 font-semibold"
                    data-testid={`remove-set-btn-${exerciseIdx}`}
                  >
                    <Minus className="w-4 h-4 mr-1" />
                    REMOVE SET
                  </Button>
                )}
              </div>
            </Card>
            );
          })()}
        </div>

        {/* Exercise navigation */}
        {exercises.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5" data-testid="exercise-navigation">
            <Button
              variant="outline"
              disabled={activeExerciseIndex === 0}
              onClick={() => setActiveExerciseIndex((index) => Math.max(0, index - 1))}
              className="glass border-stone-700 text-stone-300 hover:bg-stone-800"
              data-testid="previous-exercise-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              PREVIOUS
            </Button>

            <Button
              variant="outline"
              onClick={() => setShowWorkoutOverview(true)}
              className="hidden sm:flex glass border-stone-700 text-stone-300 hover:bg-stone-800"
            >
              <List className="w-4 h-4 mr-1" />
              OVERVIEW
            </Button>

            {activeExerciseIndex < exercises.length - 1 ? (
              <Button
                onClick={() =>
                  setActiveExerciseIndex((index) =>
                    Math.min(exercises.length - 1, index + 1)
                  )
                }
                className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold"
                data-testid="next-exercise-btn"
              >
                NEXT EXERCISE
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={saveWorkout}
                disabled={saving}
                className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold"
                data-testid="finish-workout-btn"
              >
                <Check className="w-4 h-4 mr-1" />
                {saving ? 'SAVING...' : 'FINISH WORKOUT'}
              </Button>
            )}
          </div>
        )}

        {/* Save Button (Mobile) */}
        <div className="mt-8 flex justify-center md:hidden">
          <Button
            onClick={saveWorkout}
            disabled={saving}
            className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-6 rounded-xl btn-primary tracking-wider"
            data-testid="save-workout-btn-mobile"
          >
            <Check className="w-5 h-5 mr-2" />
            {saving ? 'SAVING...' : 'COMPLETE BATTLE'}
          </Button>
        </div>
      </div>


      {/* Compact workout overview */}
      <Dialog open={showWorkoutOverview} onOpenChange={setShowWorkoutOverview}>
        <DialogContent className="bg-gradient-to-br from-stone-950 to-stone-900 border border-stone-700 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blood-400 tracking-wider">
              WORKOUT OVERVIEW
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              Jump to any exercise without leaving the active workout.
            </DialogDescription>
          </DialogHeader>

          <div className="mb-4">
            <Button
              type="button"
              onClick={regenerateCurrentWorkout}
              className="w-full bg-blood-800 hover:bg-blood-700 text-white font-bold tracking-wider"
              data-testid="overview-regenerate-workout-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              REBUILD TODAY'S WORKOUT
            </Button>
            <p className="text-stone-500 text-xs text-center mt-2">
              Generates a different version using your current workout settings.
            </p>
          </div>

          <div className="overflow-y-auto space-y-2 mt-3 pr-1">
            {exercises.map((exercise, index) => {
              const currentCompleted = exercise.sets.filter((set) => set.completed).length;
              const priorCompleted = (exercise.variation_history || []).reduce(
                (total, variation) =>
                  total + variation.sets.filter((set) => set.completed).length,
                0
              );
              const completedSets = currentCompleted + priorCompleted;

              return (
                <button
                  key={`${exercise.exercise_name}-${index}`}
                  type="button"
                  onClick={() => {
                    setActiveExerciseIndex(index);
                    setShowWorkoutOverview(false);
                  }}
                  className={`w-full text-left rounded-lg border p-3 transition-colors ${
                    index === activeExerciseIndex
                      ? 'border-blood-700 bg-blood-950/30'
                      : 'border-stone-800 bg-stone-900/50 hover:border-stone-700'
                  }`}
                  data-testid={`overview-exercise-${index}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] text-stone-500 font-bold uppercase tracking-wider">
                        Exercise {index + 1}
                      </p>
                      <p className="text-stone-200 font-semibold truncate">
                        {exercise.exercise_name}
                      </p>
                      <p className="text-stone-500 text-xs mt-1">
                        {exercise.recommended_sets} sets • {exercise.recommended_reps}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-blood-400 text-xs font-bold">
                        {completedSets} done
                      </p>
                      {index === activeExerciseIndex && (
                        <p className="text-[10px] text-stone-500 uppercase mt-1">Current</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-stone-800">
            <Label htmlFor="workout-notes" className="text-blood-400 text-sm font-bold mb-2 block uppercase tracking-wider">
              Battle Notes (Optional)
            </Label>
            <Textarea
              id="workout-notes"
              placeholder="How did the workout feel? Any observations?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-stone-900 border-stone-700 text-white min-h-[90px]"
              data-testid="workout-notes-textarea"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Rest Timer */}
      {showRestTimer && (
        <RestTimer
          onClose={() => setShowRestTimer(false)}
          onComplete={() => {
            toast.success('Rest complete! Ready for next set 💪');
            setTimeout(() => setShowRestTimer(false), 2000);
          }}
        />
      )}

      {/* Exercise Timer for Timed Exercises */}
      {showExerciseTimer && (
        <ExerciseTimer
          exerciseName={exerciseTimerData.name}
          initialDuration={exerciseTimerData.duration}
          onClose={() => setShowExerciseTimer(false)}
          onComplete={() => {
            toast.success('Exercise complete! Great work! 💪');
          }}
        />
      )}

      {/* Exercise Substitution Dialog */}
      <Dialog open={showSubstitutions} onOpenChange={setShowSubstitutions}>
        <DialogContent className="bg-gradient-to-br from-stone-950 to-stone-900 border-2 border-blood-900 text-white max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-blood-900/50 pb-4">
            <DialogTitle className="text-3xl font-bold text-blood-400 flex items-center gap-2 tracking-wider">
              <RefreshCw className="w-7 h-7 text-blood-500" />
              CHANGE EXERCISE
            </DialogTitle>
            <DialogDescription className="text-stone-400 text-base mt-2">
              Choose a suggested alternative or search for any exercise in the library
            </DialogDescription>
          </DialogHeader>
          
          {substitutions && (
            <div className="space-y-6 mt-4 overflow-y-auto pr-2">
              {/* Current Exercise - Prominent Display */}
              <div className="bg-gradient-to-r from-blood-900/30 to-blood-800/30 border-2 border-blood-700 p-5 rounded-xl">
                <p className="text-blood-300 text-sm mb-2 font-bold uppercase tracking-wider">Currently Doing:</p>
                <p className="text-blood-400 text-2xl font-bold tracking-wide">{substitutions.exercise_name}</p>
              </div>

              {/* Low-Impact Alternatives Section */}
              {substitutions.substitutions?.alternatives && substitutions.substitutions.alternatives.length > 0 && (
                <div className="bg-blood-950/30 border border-blood-800 p-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="bg-blood-700 p-2 rounded-lg">
                      <RefreshCw className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-blood-400 font-bold text-lg tracking-wider">SUGGESTED ALTERNATIVES</p>
                      <p className="text-stone-400 text-sm">{substitutions.substitutions.reason}</p>
                    </div>
                  </div>
                  <div className="grid gap-3">
                    {substitutions.substitutions.alternatives.map((alt, idx) => (
                      <button
                        key={idx}
                        onClick={() => substituteExercise(selectedExercise, alt)}
                        className="bg-gradient-to-r from-blood-900/20 to-blood-800/20 border-2 border-blood-700 hover:border-blood-600 text-white py-4 px-5 rounded-lg transition-all hover:scale-[1.02] text-left group"
                        data-testid={`alt-exercise-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-bold text-lg text-blood-400 group-hover:text-blood-300 transition-colors tracking-wide">{alt}</div>
                            <div className="text-sm text-stone-400 mt-1">
                              ⚔ Alternative movement • Click to switch
                            </div>
                          </div>
                          <ChevronRight className="w-6 h-6 text-blood-500 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Search All Exercises Section */}
              <div className="bg-stone-900/50 border border-stone-700 p-5 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="bg-stone-700 p-2 rounded-lg">
                    <Search className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-blood-400 font-bold text-lg tracking-wider">SEARCH ALL EXERCISES</p>
                    <p className="text-stone-400 text-sm">Find any exercise from the complete library</p>
                  </div>
                </div>
                
                <div className="relative mb-4">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-500" />
                  <input
                    type="text"
                    value={exerciseSearchQuery}
                    onChange={(e) => setExerciseSearchQuery(e.target.value)}
                    placeholder="Type to search... (e.g., 'dumbbell curls', 'chest', 'legs')"
                    className="w-full bg-stone-900 border-2 border-stone-700 focus:border-blood-700 text-white pl-12 pr-4 py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-900 text-base placeholder-stone-500"
                  />
                </div>

                <div className="max-h-80 overflow-y-auto space-y-2 scrollbar-thin scrollbar-thumb-blood-700 scrollbar-track-stone-900">
                  {allExercises
                    .filter(ex => 
                      exerciseSearchQuery.length === 0 || 
                      (ex.exercise_name || ex.name || '').toLowerCase().includes(exerciseSearchQuery.toLowerCase()) ||
                      (ex.muscle_group || ex.muscle || '').toLowerCase().includes(exerciseSearchQuery.toLowerCase())
                    )
                    .slice(0, 30)
                    .map((exercise, idx) => (
                      <button
                        key={idx}
                        onClick={() => substituteExercise(selectedExercise, exercise)}
                        className="bg-gradient-to-r from-stone-900 to-stone-800 border border-stone-700 hover:border-blood-800 hover:bg-blood-950/30 text-white py-4 px-5 rounded-lg transition-all hover:scale-[1.01] w-full text-left group"
                      >
                        <div className="flex items-center gap-4">
                          {/* Only show artwork that has passed the verified-media gate. */}
                          {(() => {
                            const mediaUrl = getExerciseMedia(
                              exercise.exercise_id || exercise.id,
                              exercise.muscle_group || exercise.muscle,
                              exercise.exercise_name || exercise.name,
                              exercise.image_url
                            );
                            if (!mediaUrl) return null;
                            return (
                              <div className="w-20 h-20 rounded-lg border border-stone-700 bg-stone-950 flex-shrink-0 overflow-hidden">
                                <img
                                  src={mediaUrl}
                                  alt={`${exercise.exercise_name || exercise.name} form illustration`}
                                  className="w-full h-full object-contain"
                                  loading="lazy"
                                  onError={(event) => {
                                    if (event.currentTarget.parentElement) {
                                      event.currentTarget.parentElement.style.display = 'none';
                                    }
                                  }}
                                />
                              </div>
                            );
                          })()}
                          
                          <div className="flex-1">
                            <div className="font-semibold text-base text-blood-400 group-hover:text-blood-300 transition-colors">{exercise.exercise_name || exercise.name}</div>
                            <div className="text-sm text-stone-400 mt-1 flex items-center gap-3">
                              <span className="bg-blood-950/50 border border-blood-900 px-2 py-0.5 rounded uppercase tracking-wider">{exercise.muscle_group || exercise.muscle}</span>
                              <span className="text-stone-500">• {exercise.equipment || 'Bodyweight'}</span>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-blood-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                        </div>
                      </button>
                    ))}
                  
                  {exerciseSearchQuery && allExercises.filter(ex => 
                    (ex.exercise_name || ex.name || '').toLowerCase().includes(exerciseSearchQuery.toLowerCase()) ||
                    (ex.muscle_group || ex.muscle || '').toLowerCase().includes(exerciseSearchQuery.toLowerCase())
                  ).length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-blue-300 text-lg">No exercises found for &quot;{exerciseSearchQuery}&quot;</p>
                      <p className="text-blue-400 text-sm mt-2">Try different keywords</p>
                    </div>
                  )}
                  
                  {exerciseSearchQuery.length === 0 && (
                    <p className="text-blue-400 text-sm text-center py-2 italic">
                      Start typing to search through {allExercises.length} available exercises
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Exercise Detail Modal */}
      {showExerciseDetail && selectedExerciseDetail && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-stone-700 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-stone-700 bg-stone-800">
              <div className="flex items-center gap-3">
                <Info className="w-6 h-6 text-blood-500" />
                <div>
                  <h3 className="text-lg font-bold text-white uppercase tracking-wider">
                    {selectedExerciseDetail.exercise_name}
                  </h3>
                  <p className="text-sm text-stone-400">{selectedExerciseDetail.muscle_group}</p>
                </div>
              </div>
              <button
                onClick={() => setShowExerciseDetail(false)}
                className="p-2 hover:bg-stone-700 rounded-lg transition-colors"
                data-testid="close-exercise-detail"
              >
                <X className="w-5 h-5 text-stone-300" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Form & Control Panel */}
              <div className="glass rounded-lg p-4 border border-stone-700 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 bg-blood-600 rounded-full" />
                  <h4 className="text-blood-400 text-xs font-bold uppercase tracking-widest">Form & Control</h4>
                </div>
                {(() => {
                  const form = getExerciseFormData(selectedExerciseDetail.exercise_name, selectedExerciseDetail.muscle_group);
                  return (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <p className="text-blood-500 text-[10px] font-bold uppercase tracking-wider mb-1">Setup</p>
                        <ul className="space-y-0.5">
                          {form.setup.map((tip, i) => (
                            <li key={i} className="text-stone-300 text-xs leading-relaxed flex items-start gap-1.5">
                              <span className="text-blood-700 mt-1.5 text-[4px]">&#9679;</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-blood-500 text-[10px] font-bold uppercase tracking-wider mb-1">Execution</p>
                        <ul className="space-y-0.5">
                          {form.execution.map((tip, i) => (
                            <li key={i} className="text-stone-300 text-xs leading-relaxed flex items-start gap-1.5">
                              <span className="text-blood-700 mt-1.5 text-[4px]">&#9679;</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-blood-500 text-[10px] font-bold uppercase tracking-wider mb-1">Key Cues</p>
                        <ul className="space-y-0.5">
                          {form.cues.map((tip, i) => (
                            <li key={i} className="text-stone-300 text-xs leading-relaxed flex items-start gap-1.5">
                              <span className="text-blood-700 mt-1.5 text-[4px]">&#9679;</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Target Info */}
              <div className="bg-blood-950/40 rounded-lg p-4 mb-4 border border-blood-900/50">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-stone-400 text-xs uppercase tracking-wider">Target</p>
                    <p className="text-blood-400 font-bold text-lg">
                      {selectedExerciseDetail.recommended_reps || '10-12'} {selectedExerciseDetail.is_timed ? '' : 'REPS'}
                    </p>
                  </div>
                  <div>
                    <p className="text-stone-400 text-xs uppercase tracking-wider">Sets</p>
                    <p className="text-blood-400 font-bold text-lg">
                      {selectedExerciseDetail.recommended_sets || 3} SETS
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedExercise(activeExerciseIndex);
                  setShowExerciseDetail(false);
                  getSubstitutions(
                    selectedExerciseDetail.exercise_name
                  );
                }}
                className="w-full mb-4 border-blood-800 bg-blood-950/30 hover:bg-blood-900/40 hover:border-blood-600 text-blood-300 hover:text-white font-bold tracking-wider"
                data-testid="detail-swap-exercise-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                SWAP EXERCISE
              </Button>

              {/* Form & Control above is the single authoritative coaching source. */}

              {/* Watch Tutorial Button */}
              <button
                onClick={() => {
                  setShowExerciseDetail(false);
                  openYouTubeModal(selectedExerciseDetail.exercise_name);
                }}
                className="w-full mt-4 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white rounded-lg font-bold transition-all"
              >
                <Youtube className="w-5 h-5" />
                Watch Form Tutorial on YouTube
              </button>
            </div>
          </div>
        </div>
      )}

      {/* YouTube Video Modal */}
      {showYouTubeEmbed && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-900 rounded-xl w-full max-w-2xl overflow-hidden border border-stone-700 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-stone-700 bg-stone-800">
              <div className="flex items-center gap-3">
                <Youtube className="w-6 h-6 text-red-500" />
                <div>
                  <h3 className="text-lg font-bold text-white">{youtubeExerciseName}</h3>
                  <p className="text-sm text-stone-400">Exercise form tutorials</p>
                </div>
              </div>
              <button
                onClick={() => setShowYouTubeEmbed(false)}
                className="p-2 hover:bg-stone-700 rounded-lg transition-colors"
                data-testid="close-youtube-modal"
              >
                <X className="w-5 h-5 text-stone-300" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="bg-stone-800/50 rounded-lg p-4 border border-stone-700">
                <p className="text-stone-200 text-center mb-4">
                  Watch professional tutorials for <span className="text-blood-400 font-bold">{youtubeExerciseName}</span>
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      window.open(getYouTubeSearchUrl(youtubeExerciseName), '_blank');
                      setShowYouTubeEmbed(false);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white rounded-lg font-bold transition-all shadow-lg"
                    data-testid="open-youtube-search"
                  >
                    <Youtube className="w-5 h-5" />
                    Watch on YouTube
                    <ExternalLink className="w-4 h-4 opacity-70" />
                  </button>
                </div>
              </div>
              
              {/* Quick tips */}
              <div className="text-sm text-stone-400 space-y-2">
                <p className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Search includes &quot;proper form&quot; and &quot;tutorial&quot; keywords
                </p>
                <p className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Look for videos from certified trainers
                </p>
                <p className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Pay attention to breathing cues and common mistakes
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogWorkout;
